import { User } from '../../_models/user';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import StudyResourceService from '../../_services/StudyResourceService';
import AuthenticationService from '../../_services/AuthenticationService';
import { AlertDialog } from '../../_components/alert-dialog/AlertDialog';
import { SkeletonLoader } from '../../_components/skeleton-loader/SkeletonLoader';

const SecondaryBlue = '#006ddd';

const RESOURCE_TYPE_ICONS: Record<number, string> = {
  1: "📥",  // Download
  5: "🔗",  // Reference
  6: "📝",  // Article
  13: "📚", // E-Book
  16: "🎥", // Streaming Video
  31: "🎬", // Vimeo Video
  32: "▶️",  // YouTube Video
  36: "📋", // Online Test
};

function getResourceIcon(typeId: number): string {
  return RESOURCE_TYPE_ICONS[typeId] || "📄";
}

export const TabDashboard: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [emptyMessage, setEmptyMessage] = useState<string>('');
  const [studyResourceBeans, setStudyResources] = useState<any>([]);
  const [staffResources, setStaffResources] = useState<any[]>([]);
  const [staffResourcesLoading, setStaffResourcesLoading] = useState<boolean>(true);
  const currentUser: User = AuthenticationService.currentUserValue;

  useEffect(() => {
    getDashBoards();
    fetchStaffResources();
  }, []);

  function getDashBoards() {
    setLoading(true);
    ApiService.getDashboards(currentUser?.userId)
      .then(
        respData => {
          setLoading(false);
          if (respData.data.status === 1) {
            if (respData.data.data.length > 0) {
              const beans = respData.data.data[0].studyResourceBeans;
              if (beans && beans.length > 0) {
                setStudyResources(beans);
              } else {
                setEmptyMessage("No dashboards found.");
              }
            } else {
              setEmptyMessage("No dashboards found.");
            }
          } else {
            showAlertDialog(respData.data.message);
          }
        },
        error => {
          setLoading(false);
          setEmptyMessage("No dashboards found.");
        }
      );
  }

  function fetchStaffResources() {
    setStaffResourcesLoading(true);
    ApiService.getMyStaffResources({ limit: 5 })
      .then(
        resp => {
          setStaffResourcesLoading(false);
          const data = resp.data;
          if (data.success && data.data && data.data.length > 0) {
            setStaffResources(data.data);
          }
        },
        () => {
          setStaffResourcesLoading(false);
        }
      );
  }

  function getDashBoardURL(studyResourceBean: any) {
    setLoading(true);
    ApiService.getDashBoardURL(studyResourceBean.id)
      .then(
        res => {
          const respData = res.data;
          setLoading(false);
          if (respData.status == 1) {
            window.open(respData.data.URLString, '_blank');
          } else {
            showAlertDialog(respData.message);
          }
        },
        error => {
          setLoading(false);
          showAlertDialog("Unable to open dashboard. Please try again later.");
        }
      );
  }

  function showAlertDialog(message: string) {
    setShowAlert(true);
    setAlertMessage(message);
  }

  function handleAlert() {
    setShowAlert(false);
  }

  async function openResource(resource: any) {
    const url = resource.url;
    if (!url) return;

    const isS3 = url.includes('.s3.') && url.includes('amazonaws.com');
    if (!isS3) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    const newWindow = window.open('', '_blank');
    try {
      const signedUrl = await StudyResourceService.getSignedUrl(url);
      if (newWindow) newWindow.location.href = signedUrl;
    } catch {
      if (newWindow) newWindow.location.href = url;
    }
  }

  return (
    <div>
      {showAlert && <AlertDialog title='Alert' message={alertMessage} btnOkText='OK' size='sm' close={handleAlert} />}
      <div className="tab-dashboard">
        {loading ? (
          <div style={{ padding: '20px' }}>
            <SkeletonLoader type="button" count={3} />
          </div>
        ) : emptyMessage ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'var(--text-muted)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>{emptyMessage}</div>
            <div style={{ fontSize: '14px' }}>Check back later for available dashboards.</div>
          </div>
        ) : (
          studyResourceBeans?.map((studyResourceBean: any, i: number) => (
            <div className="row" key={i}>
              <button type="button" className="btn btn-primary btn-lg btn-block"
                onClick={() => getDashBoardURL(studyResourceBean)}>
                {studyResourceBean.name}
              </button>
            </div>
          ))
        )}
      </div>

      {/* Staff-Issued Resources Summary */}
      {!staffResourcesLoading && staffResources.length > 0 && (
        <div style={{ padding: '16px 20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}>
            <h6 style={{ margin: 0, fontWeight: 600, color: 'var(--text-strong)' }}>My Resources</h6>
            <button
              style={{
                background: 'none',
                border: 'none',
                color: SecondaryBlue,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500,
                padding: 0,
              }}
              onClick={() => history.push('/my-resources')}
            >
              View All &rarr;
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {staffResources.map((resource: any) => (
              <div
                key={resource.id}
                onClick={() => openResource(resource)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  background: 'var(--surface-page)',
                  borderRadius: '8px',
                  cursor: resource.url ? 'pointer' : 'default',
                  border: '1px solid var(--border-default)',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-sunken)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-page)')}
              >
                <span style={{ fontSize: '20px', flexShrink: 0 }}>
                  {getResourceIcon(resource.typeId)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'var(--text-strong)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {resource.name}
                  </div>
                </div>
                <span style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  background: 'var(--surface-sunken)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  flexShrink: 0,
                }}>
                  {resource.typeName || 'Resource'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
